import {
	MicrosoftAuthMode,
	MicrosoftAuthStateInput,
	MicrosoftAuthStateOutput,
	MicrosoftAuthType,
} from '../../types';

type MicrosoftAuthStatePayload = {
	redirectionUrl: string;
	targetPage?: string;
	mode?: MicrosoftAuthMode;
	type?: MicrosoftAuthType;
};

const isMicrosoftAuthMode = (value: unknown): value is MicrosoftAuthMode => {
	return value === 'private' || value === 'pro';
};

const isMicrosoftAuthType = (value: unknown): value is MicrosoftAuthType => {
	return value === 'signin' || value === 'signup';
};

const encodeBase64 = (value: string) => {
	return window.btoa(
		Array.from(new TextEncoder().encode(value), (byte) => String.fromCharCode(byte)).join('')
	);
};

const decodeBase64 = (value: string) => {
	const binary = window.atob(value);
	const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
	return new TextDecoder().decode(bytes);
};

export const resolveMicrosoftAuthState = (
	input: MicrosoftAuthStateInput,
	defaults?: {
		mode?: MicrosoftAuthMode;
		type?: MicrosoftAuthType;
	}
): MicrosoftAuthStateOutput => {
	if (!input.redirectionUrl || !input.targetPage) {
		throw new Error('Invalid Microsoft auth state input.');
	}

	const resolvedMode = input.mode ?? defaults?.mode;
	const resolvedType = input.type ?? defaults?.type;

	if (!isMicrosoftAuthMode(resolvedMode) || !isMicrosoftAuthType(resolvedType)) {
		throw new Error('Microsoft auth state requires a mode and a type.');
	}

	return {
		redirectionUrl: input.redirectionUrl,
		targetPage: input.targetPage,
		mode: resolvedMode,
		type: resolvedType,
	};
};

export const encodeMicrosoftAuthState = (state: MicrosoftAuthStatePayload): string => {
	if (!state.redirectionUrl || !isMicrosoftAuthMode(state.mode) || !isMicrosoftAuthType(state.type)) {
		throw new Error('Invalid Microsoft auth state output.');
	}

	const payload: MicrosoftAuthStatePayload = {
		redirectionUrl: state.redirectionUrl,
		mode: state.mode,
		type: state.type,
	};

	if (state.targetPage) {
		payload.targetPage = state.targetPage;
	}

	return encodeBase64(JSON.stringify(payload));
};

export const decodeMicrosoftAuthStateWithFallback = (
	encodedState: string,
	fallbackRedirectionUri: string
): MicrosoftAuthStatePayload => {
	try {
		const decoded = decodeBase64(encodedState);
		const parsed = JSON.parse(decoded) as Partial<MicrosoftAuthStateOutput>;

		const payload: MicrosoftAuthStatePayload = {
			redirectionUrl: parsed.redirectionUrl || fallbackRedirectionUri,
		};

		if (typeof parsed.targetPage === 'string' && parsed.targetPage.length > 0) {
			payload.targetPage = parsed.targetPage;
		}

		if (isMicrosoftAuthMode(parsed.mode)) {
			payload.mode = parsed.mode;
		}

		if (isMicrosoftAuthType(parsed.type)) {
			payload.type = parsed.type;
		}

		return payload;
	} catch (error) {
		return {
			redirectionUrl: fallbackRedirectionUri,
		};
	}
};

export const decodeMicrosoftAuthState = (
	encodedState: string,
	fallbackRedirectionUri: string
): MicrosoftAuthStatePayload => {
	return decodeMicrosoftAuthStateWithFallback(encodedState, fallbackRedirectionUri);
};
